import { useEffect, useState } from "react";
import "./SustainabilityPractice.css";
import api from "../../../../api";
import DynamicBanner from "../../include/DynamicBanner";

const SustainabilityPractice = () => {
  const [activeTab, setActiveTab] = useState("images");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentTopicIndex, setCurrentTopicIndex] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [topics, setTopics] = useState([]);
  const [topicsData, setTopicsData] = useState([]);
  const [bannerData, setBannerData] = useState(null);

  /* ================= FETCH DATA ================= */
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get(
          "/api/sitepages/about-us/sustainability-practice",
        );
        const data = res.data || {};
        const section = data.topics_section || {};

        setBannerData(data.banner_data || null);
        setTopics(section.topics || []);
        setTopicsData(section.topicsData || []);
      } catch (err) {
        console.error(err);
      }
    };
    fetchData();
  }, []);

  /* ================= HELPERS ================= */
  const getFullImageUrl = (path) => {
    if (!path) return "/assets/images/default-banner.webp";
    if (path.startsWith("http")) return path;
    return `${process.env.REACT_APP_API_URL}/public${
      path.startsWith("/") ? path : `/${path}`
    }`;
  };

  const mergedTopics = topics.map((t) => {
    const match = topicsData.find(
      (d) => d?.title?.trim().toLowerCase() === t.title?.trim().toLowerCase(),
    ) || { images: [] };

    return { ...t, gallery: match };
  });

  const isPdf = (file) => file?.src?.toLowerCase().endsWith(".pdf");
  const isImage = (file) => /\.(jpg|jpeg|png|webp)$/i.test(file?.src);

  const filteredTopics = mergedTopics.filter((topic) => {
    if (activeTab === "reports") return topic.gallery.images.some(isPdf);
    return topic.gallery.images.some(isImage);
  });

  /* ================= IMAGE MODAL ================= */
  const openImageModal = (index) => {
    setCurrentTopicIndex(index);
    setCurrentImageIndex(0);
    setIsModalOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setIsModalOpen(false);
    document.body.style.overflow = "auto";
  };

  const navigateGallery = (dir) => {
    const images = filteredTopics[currentTopicIndex]?.gallery.images || [];
    setCurrentImageIndex(
      (prev) => (prev + dir + images.length) % images.length,
    );
  };

  const currentFile =
    filteredTopics[currentTopicIndex]?.gallery.images[currentImageIndex];

  /* ================= RENDER ================= */
  return (
    <>
      <DynamicBanner
        title={bannerData?.heading || "Sustainability Practice"}
        bgImage={bannerData?.images?.[0]}
        bgImageMobile={bannerData?.mobile_images?.[0]}
      />

      <section className="sustainability-practice pt_80 pb_80">
        <div className="container">
          <h2 className="text-center mb-4 fw-bold">Sustainability Practice</h2>

          {/* ===== TABS ===== */}
          <div className="sp-tabs-wrapper">
            <button
              className={`sp-tab ${activeTab === "images" ? "active" : ""}`}
              onClick={() => setActiveTab("images")}
            >
              Images
            </button>
            <button
              className={`sp-tab ${activeTab === "reports" ? "active" : ""}`}
              onClick={() => setActiveTab("reports")}
            >
              Files
            </button>
          </div>

          {/* ===== IMAGES TAB ===== */}
          {activeTab === "images" && (
            <div className="row g-4 justify-content-center">
              {filteredTopics.map((topic, index) => (
                <div key={index} className="col-md-4 col-sm-6">
                  <div
                    className="topic-item"
                    onClick={() => openImageModal(index)}
                  >
                    <img
                      src={getFullImageUrl(topic.image)}
                      className="topic-thumbnail"
                      alt={topic.title}
                    />
                    <div className="search-icon">🔍</div>
                    <div className="text-center p-2">
                      <strong>{topic.title}</strong>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ===== FILES TAB (PDF LIST) ===== */}
          {activeTab === "reports" && (
            <div className="pdf-list-wrapper">
              {filteredTopics.flatMap((topic) =>
                topic.gallery.images.filter(isPdf).map((file, i) => (
                  <div
                    key={`${topic.title}-${i}`}
                    className="pdf-card"
                    onClick={() =>
                      window.open(getFullImageUrl(file.src), "_blank")
                    }
                  >
                    <div className="pdf-icon">
                      <i className="fa fa-file-pdf-o" />
                    </div>
                    <div className="pdf-title">
                      {file.caption || topic.title}
                    </div>
                  </div>
                )),
              )}
            </div>
          )}
        </div>

        {/* ===== IMAGE MODAL ===== */}
        {isModalOpen && (
          <div
            className="modal show"
            onClick={(e) => e.target === e.currentTarget && closeModal()}
          >
            <button className="close-btn-sp" onClick={closeModal}>
              ×
            </button>

            <div className="modal-content sustainablity-content">
              <div className="modal-title">{currentFile?.caption}</div>
              <img
                className="modal-img"
                src={getFullImageUrl(currentFile?.src)}
                alt=""
              />
            </div>

            {filteredTopics[currentTopicIndex]?.gallery.images.length > 1 && (
              <div className="modal-nav">
                <button className="nav-btn" onClick={() => navigateGallery(-1)}>
                  ❮
                </button>
                <button className="nav-btn" onClick={() => navigateGallery(1)}>
                  ❯
                </button>
              </div>
            )}
          </div>
        )}
      </section>
    </>
  );
};

export default SustainabilityPractice;
